<template>
  <v-dialog :value="value" @input="onDialogInput" :persistent="editingSaving"
            :content-class="showConfirmation ? 'postAddDialog' : 'configuratorDialog'"
            :fullscreen="!showConfirmation && $vuetify.breakpoint.xsOnly" scrollable>
    <AddedToBasketDialog v-if="showConfirmation" :product="configuredProduct" :items="addedItems" :upgraded="upgraded"
                         @configureAgain="configureAgain" @configureProduct="configureProduct"
                         @backToProducts="backToProducts"/>
    <div v-show="!showConfirmation" ref="configuratorFrame" class="configuratorFrame">
      <DiscountBanner v-if="!mobile && hasCoupons" class="desktopConfiguratorDiscount" height-adaptive/>
      <v-card class="configuratorShell">
        <div v-if="mobile && hasCoupons" class="configuratorHeader">
          <DiscountBanner compact class="configuratorDiscount" height-adaptive/>
        </div>
        <div class="configuratorCloseRow" :class="{'is-upgrading': replacementSteps.length > 0}">
          <h2 v-if="replacementSteps.length" class="upsell-configuration-title" :aria-label="upsellConfigurationTitle">
            <span v-for="(part, index) in upsellConfigurationTitleParts" :key="index" class="upsell-title-part">{{ part }}</span>
            <span v-if="replacementSteps.length > 1" class="upsell-title-progress">({{ replacementProgress }})</span>
          </h2>
          <v-btn icon class="closeDialog" :disabled="editingSaving" :aria-label="$vuetify.lang.t('$vuetify.close')"
                 @click="onDialogInput(false)"><v-icon>close</v-icon></v-btn>
        </div>
        <v-alert v-if="editingError" type="error" dense class="ma-3">{{ editingError }}</v-alert>
        <div class="configuratorContent">
          <ProductConfigurator v-if="value" :key="`${configuredProduct.id}-${configurationVersion}`" :product="configuredProduct"
                               :prefill="configurationPrefill"
                               @backToProducts="backToProducts"
                               @addedToBasket="onAddedToBasket" @error="onConfiguratorError"/>
        </div>
      </v-card>
    </div>
  </v-dialog>
</template>

<script lang="ts">
import Component from 'vue-class-component';
import {Prop, Provide, Watch} from 'vue-property-decorator';
import BasicComponent from '@/global/BasicComponent';
import {BasketApi, BasketDto, BasketPositionDto, BasketReplacementDto, ConfiguredProductDto, ProductDto} from '@swagger';
import ProductConfigurator from './ProductConfigurator.vue';
import DiscountBanner from '@/components/DiscountBanner.vue';
import AddedToBasketDialog from './AddedToBasketDialog.vue';
import {AddedBasketItem} from '@/global/AddedBasketItem';
import {BasketProductSubmission, RecommendedProductSelection} from '@/global/BasketProductSubmission';
import {ConfigurationChoices, ProductConfigurationPrefill, sourcePersonCodes} from '@/global/ProductConfigurationPrefill';

@Component({components: {ProductConfigurator, DiscountBanner, AddedToBasketDialog}})
export default class ProductConfigurationDialog extends BasicComponent {
  @Prop(Boolean) value: boolean;
  @Prop(Object) product: ProductDto;
  @Prop(Object) basketPosition: BasketPositionDto;
  editingError: string | null = null;
  editingSaving = false;

  created() {
    if (!this.basketPosition) {
      return;
    }
    const configuration = JSON.parse(JSON.stringify(this.basketPosition.product));
    const personCodes = sourcePersonCodes([configuration], this.allPersons);
    this.configurationPrefill = {configuration, personCodes, choices: {
      personCode: personCodes.length === 1 ? personCodes[0] : undefined,
      mode: configuration.detailedConfiguration ? "DETAILED" : "AUTO"}};
    this.submissionContext.editing = true;
    this.submissionContext.submit = this.submitEdit.bind(this);
  }

  onConfiguratorError(message: string) {
    if (this.basketPosition) {
      this.editingError = message;
    } else {
      this.$emit("error", message);
    }
  }

  async submitEdit(products: ConfiguredProductDto[]): Promise<void> {
    if (this.editingSaving) {
      return;
    }
    this.editingError = null;
    if (products.length !== 1) {
      this.editingError = this.$t("BASKET_EDIT_SINGLE_CONFIGURATION").toString();
      return;
    }
    this.editingSaving = true;
    try {
      const basket = await new BasketApi(this.apiConfiguration).editPosition({
        positionId: this.basketPosition.id, product: products[0]});
      this.$store.commit("setBasket", basket);
      this.$emit("addedToBasket");
      this.$emit("input", false);
    } catch (failure) {
      const detail = typeof failure.json === "function" ? await failure.json().catch(() => null) : null;
      this.editingError = detail?.message ?? String(failure.message ?? failure);
    } finally {
      this.editingSaving = false;
    }
  }

  showConfirmation = false;
  upgraded = false;
  configurationVersion = 0;
  addedItems: AddedBasketItem[] = [];
  recommendationProduct: ProductDto | null = null;
  @Provide('basketProductSubmission') submissionContext: BasketProductSubmission = {submit: null};
  replacementSources: AddedBasketItem[] = [];
  replacements: BasketReplacementDto[] = [];
  configurationPrefill: ProductConfigurationPrefill | null = null;
  replacementSteps: AddedBasketItem[] = [];
  replacementStep = 0;
  replacementConfigurations: Record<number, ConfiguredProductDto[]> = {};
  replacementChoices: Record<number, ConfigurationChoices[]> = {};

  get configuredProduct(): ProductDto {
    return this.recommendationProduct ?? this.product;
  }

  onAddedToBasket(items: AddedBasketItem[]) {
    this.upgraded = this.replacements.length > 0;
    const basket: BasketDto = this.$store.getters.basket;
    const remaining = this.replacementSources.flatMap(item => {
      const position = basket.positions.find(position => position.id === item.position.id);
      const replacedAmount = this.replacements.find(replacement => replacement.positionId === item.position.id)?.amount ?? 0;
      const amount = item.amount - replacedAmount;
      return position && amount > 0 ? [{...item, amount, position}] : [];
    });
    const confirmed = new Map(items.map(item => [item.position.id, item]));
    remaining.forEach(item => {
      if (!confirmed.has(item.position.id)) {
        confirmed.set(item.position.id, item);
      }
    });
    this.addedItems = Array.from(confirmed.values());
    this.clearReplacement();
    this.showConfirmation = true;
    this.$emit('addedToBasket');
  }

  configureAgain() {
    this.upgraded = false;
    this.clearReplacement();
    this.configurationVersion++;
    this.showConfirmation = false;
    this.$nextTick(() => (this.$refs.configuratorFrame as HTMLElement)?.querySelector('button')?.focus());
  }

  configureProduct(selection: RecommendedProductSelection) {
    this.recommendationProduct = selection.product;
    this.configureAgain();
    this.replacements = selection.replacements;
    this.replacementSources = selection.items;
    this.replacementSteps = this.replacements.map(replacement => selection.items.find(item => item.position.id === replacement.positionId))
      .filter(Boolean);
    if (this.replacements.length) {
      this.setReplacementPrefill();
      this.submissionContext.submit = this.submitUpgrade.bind(this);
    }
  }

  get replacementProgress(): string {
    return this.$t('UPSELL_CONFIGURATION_PROGRESS').toString().replace('{current}', String(this.replacementStep + 1))
      .replace('{total}', String(this.replacementSteps.length));
  }

  get upsellConfigurationTitleParts(): string[] {
    const source = this.replacementSteps[this.replacementStep];
    const name = source ? this.$store.getters.product(source.configuration.productId)?.name ?? '' : '';
    const title = this.$t('UPSELL_CONFIGURATION_TITLE').toString().replace('{name}', name);
    const separator = title.indexOf(':');
    return separator >= 0 ? [title.slice(0, separator + 1), title.slice(separator + 1).trim()] : [title];
  }

  get upsellConfigurationTitle(): string {
    const title = this.upsellConfigurationTitleParts.join(' ');
    return this.replacementSteps.length > 1 ? `${title} (${this.replacementProgress})` : title;
  }

  setReplacementPrefill() {
    const source = this.replacementSteps[this.replacementStep];
    const inferredCodes = source ? sourcePersonCodes([source.configuration], this.allPersons) : [];
    const personCode = source?.choices?.personCode;
    this.configurationPrefill = source ? {configuration: source.configuration,
      personCodes: personCode ? inferredCodes.filter(code => code === personCode) : inferredCodes,
      choices: source.choices} : null;
  }

  clearReplacement() {
    this.submissionContext.submit = null;
    this.replacements = [];
    this.replacementSources = [];
    this.configurationPrefill = null;
    this.replacementSteps = [];
    this.replacementStep = 0;
    this.replacementConfigurations = {};
    this.replacementChoices = {};
  }

  async submitUpgrade(configurations: ConfiguredProductDto[], success: (items: AddedBasketItem[]) => void,
                      error: (message: string) => void, choices: ConfigurationChoices[] = []): Promise<void> {
    const source = this.replacementSteps[this.replacementStep];
    const replacement = this.replacements.find(replacement => replacement.positionId === source?.position.id);
    if (!replacement || this.replacementSteps.length !== this.replacements.length) {
      error(this.$t('UPSELL_REPLACEMENT_UNAVAILABLE').toString());
      return;
    }
    const amount = replacement.amount;
    const products = configurations.length === 1 ? Array.from({length: amount}, () => configurations[0]) : configurations;
    if (!products.length || products.length !== amount) {
      error(this.$t('UPSELL_CONFIGURATION_AMOUNT').toString().replace('{amount}', String(amount)));
      return;
    }
    this.replacementConfigurations[replacement.positionId] = products;
    this.replacementChoices[replacement.positionId] = configurations.length === 1
      ? products.map(() => choices[0]) : choices;
    if (this.replacementStep < this.replacementSteps.length - 1) {
      this.replacementStep++;
      this.setReplacementPrefill();
      this.configurationVersion++;
      return;
    }
    const allProducts = this.replacements.flatMap(replacement => this.replacementConfigurations[replacement.positionId] ?? []);
    const allChoices = this.replacements.flatMap(replacement => this.replacementChoices[replacement.positionId] ?? []);
    try {
      const product = this.configuredProduct;
      const version = this.configurationVersion;
      const beforeAmounts = new Map<number, number>((this.$store.getters.basket?.positions ?? [])
        .map(position => [position.id, position.amount]));
      const result = await new BasketApi(this.apiConfiguration).upgradeProducts({products: allProducts, replacements: this.replacements});
      this.$store.commit('setBasket', result.basket);
      const upgradedIds = new Set(result.upgradedPositionIds);
      const items: AddedBasketItem[] = result.basket.positions.filter(position => upgradedIds.has(position.id))
        .map(position => {
          // The server expands automatic sets into leaf products. Keep the
          // customer's chosen mode for a later recommendation when it matches.
          const submittedIndex = allProducts.findIndex(configuration => configuration.productId === position.product.productId
            && configuration.images.every(image => position.product.images.some(canonical =>
              canonical.uniqueImageId === image.uniqueImageId && (canonical.imageCropId ?? null) === (image.imageCropId ?? null)
              && canonical.filter === (this.$store.getters.product(canonical.productId)?.imageFilter
                ?? product.imageFilter ?? image.filter ?? 'NONE') && (!configuration.detailedConfiguration || canonical.productId === image.productId))));
          const configuration = allProducts[submittedIndex] ?? position.product;
          const amount = position.amount - (beforeAmounts.get(position.id) ?? 0);
          return {position, amount, configuration: {...configuration, images: configuration.images.map(image => ({...image}))},
            choices: allChoices[submittedIndex]};
        }).filter(item => item.amount > 0);
      if (this.value && version === this.configurationVersion && this.configuredProduct.id === product.id) success(items);
    } catch (failure) {
      const detail = typeof failure.json === 'function' ? await failure.json().catch(() => null) : null;
      error(detail?.message ?? String(failure.message ?? failure));
    }
  }

  @Watch('value')
  onOpenChange() {
    this.upgraded = false;
    this.clearReplacement();
    this.showConfirmation = false;
    this.recommendationProduct = null;
  }

  @Watch('product.id')
  onProductChange() {
    this.upgraded = false;
    this.clearReplacement();
    this.showConfirmation = false;
    this.recommendationProduct = null;
  }

  backToProducts() {
    if (this.editingSaving) {
      return;
    }
    this.$emit('input', false);
    if (!this.basketPosition) {
      this.goToStep(3);
    }
  }

  onDialogInput(open: boolean) {
    if (this.editingSaving) {
      return;
    }
    if (!open && this.showConfirmation) {
      this.backToProducts();
    } else {
      this.$emit('input', open);
    }
  }

}
</script>
<style lang="scss">
@use "../../stylesheets/Variables.scss" as *;
.v-dialog.postAddDialog {
  width: 900px;
  max-width: calc(100% - 48px);
  max-height: 90dvh;
  overscroll-behavior-y: contain;

  > .added-to-basket-dialog {
    max-height: 90dvh;
  }

  @media (max-width: 959px) {
    position: absolute;
    bottom: 0;
    margin: 0;
    width: 100%;
    max-width: 100%;
    max-height: 100%;
    border-radius: 0;
    overflow: hidden;
    > .added-to-basket-dialog {
      max-height: 100dvh;
    }
  }
}

.v-dialog.configuratorDialog {
  // Let the transparent space beside the discount banner reach the backdrop.
  pointer-events: none;
  .desktopConfiguratorDiscount, .configuratorShell {
    pointer-events: auto;
  }
  display: flex;
  box-shadow: none;
  position: relative;
  line-height: inherit !important;
  overflow: visible;

  &:not(.v-dialog--fullscreen) {
    flex: 0 1 auto;
    min-width: 0;
    width: calc(100% - 48px);
    max-width: calc(100% - 48px);
    margin: 24px;
  }

  .configuratorFrame {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    min-width: 0;
    max-height: inherit;
  }
  .desktopConfiguratorDiscount {
    flex: 0 0 auto;
    align-self: flex-end;
    margin: 0 0 16px;
    position: relative;
    z-index: 12;
  }
  .configuratorShell {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
  }
  .configuratorHeader {
    flex: 0 0 auto;
    background: $shop-white;
    position: relative;
    z-index: 12;
  }
  .configuratorDiscount {
    width: 100%;
    max-width: none;
    margin: 0;
  }
  .configuratorCloseRow {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: flex-end;
    padding: 4px 8px 0;
  }
  .upsell-configuration-title {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    column-gap: .25em;
    flex: 1;
    min-width: 0;
    margin: 0 0 0 16px;
    font-size: 18px;
    font-weight: 600;
    line-height: 1.4;
    word-break: normal;
    .upsell-title-part {
      max-width: 100%;
    }
    .upsell-title-progress {
      white-space: nowrap;
    }
    + .closeDialog {
      margin-left: 16px;
    }
  }
  .closeDialog {
    flex: none;
    margin-left: auto;
    .v-icon {
      margin: 0;
    }
  }
  .configuratorContent {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior-y: contain;
  }
  @media (min-width: 960px) {
    &:not(.v-dialog--fullscreen) .configuratorCloseRow.is-upgrading {
      position: relative;
      min-height: 40px;
      padding-inline: 60px;

      .upsell-configuration-title {
        justify-content: center;
        text-align: center;
        margin-left: 0;
      }
      .closeDialog {
        position: absolute;
        top: 4px;
        right: 8px;
        margin-left: 16px;
      }
    }
  }

  @media (max-height: 650px) {
    .desktopConfiguratorDiscount {
      margin-bottom: 8px;
    }
  }

  &.v-dialog--fullscreen {
    width: 100%;
    max-width: 100%;
    margin: 0;
    background-color: $shop-white;
    .configuratorFrame {
      height: 100%;
      max-height: 100%;
    }
    .configuratorShell {
      border-radius: 0;
    }
  }

  @media screen and (max-width: 959px) {

    .productConfigurator {
      display: flex;
      flex-direction: column;
      flex: 1 1 auto;
      min-height: 0;

      > .setConfigurator, > .basicConfigurator {
        flex: 1 0 auto;
      }
    }

    .setConfigurator {
      flex-direction: column;
    }

    .configurationStepper {
      flex: 1 0 auto;

      .v-stepper__step {
        padding: 16px;
      }

      &.v-stepper--vertical .v-stepper__content {
        // Vuetify's negative right margin extends the gallery beyond the dialog.
        margin-left: 8px;
        margin-right: 8px;
        padding-left: 0;
        padding-right: 0;
        min-width: 0;
      }
    }
  }
}
</style>
